import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, LeafIcon, RotateCcwIcon, TruckIcon } from 'lucide-react';
import { productBenefits } from '../../data/productBenefits';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct } from '../../utils/products';
import type { Product } from '../../types/product';
import { Stars } from '../shop/Stars';
import { QuantityStepper } from '../shop/QuantityStepper';
const badgeLabel: Record<string, string> = {
  bestseller: 'Bestseller',
  nieuw: 'Nieuw',
  cadeau: 'Cadeau-tip'
};
export function BuyBox({
  product
}: {
  product: Product;
}) {
  const {
    add
  } = useCart();
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState(product.variants[0].id);
  const [qty, setQty] = useState(1);
  const variant = product.variants.find(v => v.id === variantId) ?? product.variants[0];
  const included = (product.includes ?? []).map(s => getProduct(s)).filter((p): p is Product => Boolean(p));
  const isSizeVariant = /\d/.test(variant.label);
  const why = productBenefits[product.slug] ?? [];
  const buyNow = () => {
    add(product.slug, variant.id, qty, false);
    navigate('/afrekenen');
  };
  return <div data-id="element-267" data-mp="18wmd9o470xkv">
      {product.badges.length > 0 && <div className="flex gap-2" data-id="element-268" data-mp="14rwjq5x0bv34">
          {product.badges.map(b => <span key={b} className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium" data-id="element-269" data-mp="zud6n9c1pocr">{badgeLabel[b]}</span>)}
        </div>}
      <h1 className="mt-4 font-display text-4xl uppercase tracking-display text-ink sm:text-5xl" data-id="element-270" data-mp="v1jz6dp701wz">{product.name}</h1>
      <p className="mt-1.5 text-lg text-muted" data-id="element-271" data-mp="q1z4wgkywhql">{product.subtitle}</p>
      <div className="mt-4" data-id="element-272" data-mp="l69x5pysnuj7">
        <a href="#details" className="inline-block" data-id="element-273" data-mp="l2vyost945h1">
          <Stars rating={product.rating} reviews={product.reviews} size="md" data-id="element-274" data-mp="h2bythhamnr7" />
        </a>
      </div>

      <p className="mt-6 flex items-baseline gap-3" data-id="element-275" data-mp="c5bv3d7x1x39">
        <span className="text-3xl font-semibold tracking-tight tabular-nums" data-id="element-276" data-mp="2godvu39nf7a">{formatPrice(variant.price * qty)}</span>
        {variant.compareAt && <span className="text-lg text-muted line-through" data-id="element-277" data-mp="6jba076yzmaa">{formatPrice(variant.compareAt * qty)}</span>}
        {qty > 1 && <span className="text-sm text-muted" data-id="element-278" data-mp="3op6d19e0a845">{qty} × {formatPrice(variant.price)}</span>}
      </p>
      <p className="mt-4 text-lg font-light leading-relaxed text-ink" data-id="element-279" data-mp="3tnr68ouubrj3">{product.benefit}</p>
      {why.length > 0 && <ul className="mt-5 space-y-2.5" aria-label="Waarom het goed voor je is" data-id="element-280" data-mp="l7xxiw6r3kcl">
          {why.map(w => <li key={w.title} className="flex items-start gap-3 text-[15px]" data-id="element-281" data-mp="q827e3d5lgtu">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-blush text-berry" data-id="element-282" data-mp="bh31x17m78eh">
                <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-283" data-mp="ged29bwjl4h3" />
              </span>
              {w.title}
            </li>)}
        </ul>}

      {product.variants.length > 1 && <fieldset className="mt-8" data-id="element-284" data-mp="2tn5r8da5b9zw">
          <legend className="text-sm font-medium" data-id="element-285" data-mp="2tm48dmmw1j7m">
            {isSizeVariant ? 'Inhoud' : 'Variant'}: <span className="text-muted" data-id="element-286" data-mp="2omzyets53hg8">{variant.label}</span>
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-287" data-mp="32fzdiuo0f9n9">
            {product.variants.map(v => {
          const active = v.id === variant.id;
          return <button key={v.id} type="button" role="radio" aria-checked={active} onClick={() => setVariantId(v.id)} className={`flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors duration-150 ${active ? 'border-ink bg-white ring-1 ring-ink' : 'border-line bg-white hover:border-muted'}`} data-id="element-288" data-mp="2ao087hozauhy">
                  <span className="text-sm font-semibold" data-id="element-289" data-mp="3q522knkz44rs">{v.label}</span>
                  <span className="text-sm text-muted" data-id="element-290" data-mp="3q4s92q0pzfhc">{formatPrice(v.price)}</span>
                </button>;
        })}
          </div>
        </fieldset>}

      {included.length > 0 && <div className="mt-8" data-id="element-291" data-mp="uaqr2e04dobx">
          <p className="text-sm font-medium" data-id="element-292" data-mp="z7h40sgmo3t4">In deze set</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-293" data-mp="za3eazuwkxf1">
            {included.map(p => <li key={p.slug} data-id="element-294" data-mp="148end8ddj3w7">
                <Link to={`/product/${p.slug}`} className="flex items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-3 text-sm hover:border-ink" data-id="element-295" data-mp="188yi65ah5vf6">
                  <img src={p.image} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-296" data-mp="1d5ymaanrko3n" />
                  {p.name}
                </Link>
              </li>)}
          </ul>
        </div>}

      <div className="mt-8 flex gap-3" data-id="element-297" data-mp="1mznzlpej46rh">
        <QuantityStepper value={qty} onChange={setQty} size="lg" data-id="element-298" data-mp="1rxh1454w0clf" />
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-14 flex-1 whitespace-nowrap rounded-full bg-ink px-6 text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry" data-id="element-299" data-mp="1ruuxggl4ah8h">
          In winkelwagen
        </button>
      </div>
      <button type="button" onClick={buyNow} className="mt-3 h-14 w-full whitespace-nowrap rounded-full border border-ink/15 bg-white text-base font-medium text-ink transition-colors duration-150 hover:border-ink" data-id="element-300" data-mp="37f3uffdw3ruh">
        Direct afrekenen
      </button>

      <p className="mt-5 flex items-center gap-2 text-sm" data-id="element-301" data-mp="3v580ruigq5p6">
        <span className="h-2 w-2 rounded-full bg-olive" aria-hidden="true" data-id="element-302" data-mp="3v5q0500f901b" />
        <span data-id="element-303" data-mp="3qdg6pa03lhua">
          <strong className="font-medium text-olive" data-id="element-304" data-mp="3qcwzoobzkdv1">Op voorraad</strong> — vóór 22:00 besteld, morgen in huis
        </span>
      </p>

      <ul className="mt-8 grid gap-3 border-t border-line pt-6 text-sm text-muted" data-id="element-305" data-mp="3ldk8xshzumn6">
        <li className="flex items-center gap-3" data-id="element-306" data-mp="mrhi2lvizrne"><TruckIcon className="h-4 w-4 text-ink" data-id="element-307" data-mp="hy65s57y2io3" /> Gratis verzending vanaf € 50</li>
        <li className="flex items-center gap-3" data-id="element-308" data-mp="2c6s5qky82ce3"><RotateCcwIcon className="h-4 w-4 text-ink" data-id="element-309" data-mp="2c7thkrn2b2wv" /> 30 dagen bedenktijd</li>
        <li className="flex items-center gap-3" data-id="element-310" data-mp="2g8ddxszta2hy"><LeafIcon className="h-4 w-4 text-ink" data-id="element-311" data-mp="2g7c23maz1bz6" /> 100% puur, zonder toevoegingen</li>
      </ul>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-canvas/95 px-5 py-3 backdrop-blur lg:hidden" data-id="element-312" data-mp="22g78b0uabdwr">
        <div className="min-w-0 flex-1" data-id="element-313" data-mp="1sisrv18d2p4g">
          <p className="truncate text-sm font-medium" data-id="element-314" data-mp="1shreur7sbsk3">{product.name} · {variant.label}</p>
          <p className="text-sm font-semibold tabular-nums" data-id="element-315" data-mp="1xip1bfztv8re">{formatPrice(variant.price * qty)}</p>
        </div>
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-12 whitespace-nowrap rounded-full bg-ink px-6 text-sm font-medium text-canvas" data-id="element-316" data-mp="1jjfis9o5g2k9">
          In winkelwagen
        </button>
      </div>
    </div>;
}